import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { queryKeys } from '@core/api/queryKeys';
import { buildQueryString } from '@shared/lib/buildQueryString';

/**
 * Response of `GET /api/v1/stats/last-update`. `lastUpdateAt` is the BE's data-freshness
 * watermark = MAX(updated_at) across the user's trades + correlations (account-scoped when an
 * `account` is passed), as an ISO-8601 string, or `null` when the user has no data.
 */
export interface LastUpdate {
  lastUpdateAt: string | null;
}

/**
 * The dashboard "Last update" stamp. Scoped by **account only** (NOT the date range) — the import
 * time is independent of the view window. Re-fetched live on `trades.changed` / `correlations.changed`
 * via `useRealtimeInvalidation` (`['last-update']`), so a CQG fill — including an open-position entry
 * that has no correlation yet — ticks the stamp.
 */
export function useLastUpdate(params: { account?: string } = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.lastUpdate(params),
    queryFn:  () => api.get<LastUpdate>(`/api/v1/stats/last-update${buildQueryString(params)}`),
  });
}
